import { Call, Device } from '@twilio/voice-sdk';
import { createAction } from 'apps/tasks/utils/tasks-actions';
import Api from 'entities/helpers/api';
import EverTrue from 'app';
import ErrorLogger from 'entities/helpers/error-log-helper';

const TOKEN_REFRESH_RETRY_DELAY_MS = 1500;
const TOKEN_REFRESH_MAX_ATTEMPTS = 2;

const refreshDeviceToken = async (twilioDevice) => {
  for (let attempt = 1; attempt <= TOKEN_REFRESH_MAX_ATTEMPTS; attempt++) {
    try {
      const { token } = await Api.OUTREACH.VOIP_TOKEN.get();
      twilioDevice.updateToken(token);
      return;
    } catch (error) {
      if (attempt === TOKEN_REFRESH_MAX_ATTEMPTS) {
        ErrorLogger.error('Twilio token refresh failed', { extra: { error, page_name: 'dialer' } });
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, TOKEN_REFRESH_RETRY_DELAY_MS));
    }
  }
};

export const establishTwilioDevice = async () => {
  // Fetch unique token and instantiate Device
  const { token } = await Api.OUTREACH.VOIP_TOKEN.get();
  // Prefer Opus for its packet-loss concealment; fall back to PCMU for carriers/edges that don't accept Opus.
  const twilioDevice = new Device(token, {
    codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
  });

  // Register the device to activate its lifecycle (important for tokenWillExpire and other events)
  twilioDevice.register();

  // EVENT LISTENERS

  // If there is an error, show a message to user and log it in Sentry
  twilioDevice.on('error', (error) => {
    EverTrue.Alert.error('Sorry, there was an issue with our Caller integration. Please try again soon.', {
      id: 4,
      timeout: 10000,
    });
    ErrorLogger.error('Twilio.Device Error', { extra: { error, page_name: 'dialer' } });
  });

  // Retry once on transient failure; without this, a single network blip during refresh drops any in-flight call.
  twilioDevice.on('tokenWillExpire', () => refreshDeviceToken(twilioDevice));

  return twilioDevice;
};

export const createActionFromCall = async (callObject, answeredAt, contactId, activeCallNotes) => {
  const endTime = new Date().getTime();

  // answeredAt is set by the caller's own 'accept' event handler, so it's an authoritative
  // record of whether the callee answered. Prefer it over Twilio's undocumented, private
  // _isAnswered/_wasConnected flags on the call object, which are suspected of going stale
  // on some answered calls (SIG-898).
  const twoWayCommunication = !!answeredAt;
  // Duration is measured from when the callee answered, so it reflects talk time, not ring time.
  const durationInSeconds = twoWayCommunication ? Math.floor((endTime - answeredAt) / 1000) : 0;

  const action = {
    contact_id: contactId,
    two_way_communication: twoWayCommunication,
    duration_in_seconds: durationInSeconds,
    status: 'SUCCESS',
    call_notes: activeCallNotes,
    // CallSid of the client (browser) leg — the join key that lets the backend
    // correlate this record with Twilio call/status webhooks and Voice Insights.
    twilio_call_sid: callObject?.parameters?.CallSid,
  };

  return await createAction(action);
};

/**
 * Record a call attempt that never connected (no-answer / busy / declined / canceled / failed),
 * so it still appears in call history. The precise outcome + SIP code live in call_error_events,
 * joinable by the CallSid; `status` here is the coarse client-side label.
 */
export const createUnconnectedCallAction = async (callObject, contactId, status) => {
  const action = {
    contact_id: contactId,
    two_way_communication: false,
    duration_in_seconds: 0,
    status,
    call_notes: '',
    twilio_call_sid: callObject?.parameters?.CallSid,
  };

  return await createAction(action);
};

export const updateCall = async (id, payload) => {
  try {
    await Api.OUTREACH.CALL.put({
      urlArgs: { id },
      data: JSON.stringify(payload),
    });
  } catch (error) {
    ErrorLogger.error('Error updating call', { extra: { error, page_name: 'dialer' } });
  }
};
